import React, { useMemo, useRef, useState } from "react";

import useEsTelefono from "../../../../utils/esTelefono";
import S from "./strings";
import { Opciones, horaBonita } from "./comun";

/** Un día del taller mientras se arma: fecha "YYYY-MM-DD", horas "HH:MM" (24 h), aula y maestra (0 = sin asignar). */
export interface DiaDraft { date: string; startTime: string; endTime: string; classroomId: number; mentorId: number; }

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MES_CORTO = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const DIA_CORTO = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
// La semana arranca en lunes, como el calendario de los horarios
const CABECERA = ["Lu", "Ma", "Mi", "Ju", "Vi", "Sá", "Do"];

const ymd = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const deYmd = (s: string): Date => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
export const hoyYmd = (): string => ymd(new Date());

/** "2026-10-26" -> {dia: "Lun 26 oct", anio: "2026"} */
const etiquetaDia = (s: string): { dia: string; anio: string } => {
	const d = deYmd(s);
	return { dia: `${DIA_CORTO[d.getDay()]} ${d.getDate()} ${MES_CORTO[d.getMonth()]}`, anio: String(d.getFullYear()) };
};

/** Horas entre "09:00" y "13:30" (0 si falta alguna o la salida no es después). */
export const horasEntre = (a: string, b: string): number => {
	const m1 = /^(\d{1,2}):(\d{2})/.exec(a || ""), m2 = /^(\d{1,2}):(\d{2})/.exec(b || "");
	if (!m1 || !m2) return 0;
	const min = (Number(m2[1]) * 60 + Number(m2[2])) - (Number(m1[1]) * 60 + Number(m1[2]));
	return min > 0 ? min / 60 : 0;
};
export const horasTexto = (h: number): string => `${Number.isInteger(h) ? h : h.toFixed(1)} h`;

// Cada media hora de 6:00 am a 10:00 pm: se elige con un clic en vez de escribirla
const HORAS: string[] = Array.from({ length: 33 }, (_x, i) => { const m = 360 + i * 30; return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`; });

/** Hora con un solo selector en am/pm. Valor "HH:MM" (24 h). */
export const HoraSelect: React.FC<{ value: string; onChange: (v: string) => void; label: string; vacia?: string }> = ({ value, onChange, label, vacia }) => {
	const lista = value && !HORAS.includes(value.substring(0, 5)) ? [...HORAS, value.substring(0, 5)].sort() : HORAS;
	return (
		<select className="wau-entrada wau-num" aria-label={label} value={(value || "").substring(0, 5)} onChange={e => onChange(e.target.value)}>
			<option value="">{vacia ?? "—"}</option>
			{lista.map(h => <option key={h} value={h}>{horaBonita(h)}</option>)}
		</select>
	);
};

export const diaValido = (d: DiaDraft): boolean => /^\d{4}-\d{2}-\d{2}$/.test(d.date) && d.startTime !== "" && horasEntre(d.startTime, d.endTime) > 0;

interface Props {
	value: DiaDraft[];
	onChange: (v: DiaDraft[]) => void;
	opciones: Opciones;
	/** Valores con los que entra cada día nuevo (al agregar días a un taller: los de su última sesión). */
	base?: Partial<DiaDraft>;
	/** Días que el taller ya tiene: se ven marcados y no se pueden volver a elegir. */
	ocupados?: string[];
}

/**
 * Días del taller (Esteban, 2026-10-23): se tocan en el calendario, cada uno con su entrada, salida, aula y maestra (pueden
 * cambiar por día). Lo de arriba es lo que toma cada día nuevo y "Aplicar a Todos" lo pone en todos.
 */
const DiasEditor: React.FC<Props> = ({ value, onChange, opciones, base, ocupados }) => {
	const movil = useEsTelefono();
	const [b, setB] = useState<Omit<DiaDraft, "date">>({
		startTime: base?.startTime || "09:00", endTime: base?.endTime || "13:00", classroomId: base?.classroomId || 0, mentorId: base?.mentorId || 0
	});
	const inicial = value[0]?.date || base?.date || hoyYmd();
	const [mes, setMes] = useState<{ y: number; m: number }>(() => { const d = deYmd(inicial); return { y: d.getFullYear(), m: d.getMonth() }; });
	const hoy = hoyYmd();
	// Lo último que se mandó: dos clics seguidos antes de que la pantalla se vuelva a pintar no se pisan
	const actual = useRef<DiaDraft[]>(value);
	actual.current = value;
	const cambiarTodo = (v: DiaDraft[]) => { actual.current = v; onChange(v); };
	const elegidos = useMemo(() => new Set(value.map(d => d.date)), [value]);
	const ya = useMemo(() => new Set(ocupados || []), [ocupados]);

	const ordenar = (xs: DiaDraft[]) => [...xs].sort((x, y) => (x.date + x.startTime).localeCompare(y.date + y.startTime));
	const alternar = (fecha: string) => {
		if (ya.has(fecha)) return;
		const v = actual.current;
		cambiarTodo(v.some(d => d.date === fecha) ? v.filter(d => d.date !== fecha) : ordenar([...v, { date: fecha, ...b }]));
	};
	const cambiar = (fecha: string, k: keyof DiaDraft, v: any) => cambiarTodo(actual.current.map(d => d.date === fecha ? { ...d, [k]: v } : d));
	const aplicarATodos = () => cambiarTodo(actual.current.map(d => ({ ...d, ...b })));

	// Celdas del mes: huecos antes del día 1 para que la semana empiece en lunes
	const primero = new Date(mes.y, mes.m, 1);
	const huecos = (primero.getDay() + 6) % 7;
	const diasMes = new Date(mes.y, mes.m + 1, 0).getDate();
	const celdas: (string | null)[] = [...Array(huecos).fill(null), ...Array.from({ length: diasMes }, (_x, i) => ymd(new Date(mes.y, mes.m, i + 1)))];
	const mover = (n: number) => setMes(x => { const d = new Date(x.y, x.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });
	/** Tocar "Lu" elige (o quita) todos los lunes de este mes que no han pasado. */
	const columna = (col: number) => {
		const fechas = celdas.filter((c, i): c is string => c !== null && i % 7 === col && c >= hoy && !ya.has(c));
		if (fechas.length === 0) return;
		const v = actual.current;
		const todos = fechas.every(f => v.some(d => d.date === f));
		cambiarTodo(todos ? v.filter(d => !fechas.includes(d.date)) : ordenar([...v, ...fechas.filter(f => !v.some(d => d.date === f)).map(f => ({ date: f, ...b }))]));
	};

	const aulas = opciones.aulas.map(a => ({ id: a.id, name: a.buildingName ? `${a.name}, ${a.buildingName}` : a.name }));
	const maestras = opciones.maestras.map(m => ({ id: m.id, name: m.name }));
	const selAula = (v: number, on: (n: number) => void, label: string) => (
		<select className="wau-entrada" aria-label={label} value={v} onChange={e => on(Number(e.target.value))}>
			<option value={0}>{S.sinAula}</option>
			{aulas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
		</select>
	);
	const selMaestra = (v: number, on: (n: number) => void, label: string) => (
		<select className="wau-entrada" aria-label={label} value={v} onChange={e => on(Number(e.target.value))}>
			<option value={0}>{S.sinMaestra}</option>
			{maestras.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
		</select>
	);
	const total = value.reduce((s, d) => s + horasEntre(d.startTime, d.endTime), 0);

	return (
		<div className={"wte-dias" + (movil ? " angosto" : "")}>
			<div className="wte-cal" aria-label={S.calendario}>
				<div className="wte-cal-cab">
					<button type="button" aria-label={S.mesAnterior} onClick={() => mover(-1)}>‹</button>
					<span>{MESES[mes.m]} {mes.y}</span>
					<button type="button" aria-label={S.mesSiguiente} onClick={() => mover(1)}>›</button>
				</div>
				<div className="wte-cal-sem">
					{CABECERA.map((c, i) => <span key={c} role="button" tabIndex={0} style={{ cursor: "pointer" }} title={S.todosLos(c)}
						onClick={() => columna(i)} onKeyDown={e => { if (e.key === "Enter") columna(i); }}>{c}</span>)}
				</div>
				<div className="wte-cal-mes">
					{celdas.map((c, i) => c === null ? <span key={"h" + i} /> : (
						<button key={c} type="button" aria-pressed={elegidos.has(c)} disabled={ya.has(c)}
							className={"wte-cal-dia" + (elegidos.has(c) ? " on" : "") + (ya.has(c) ? " ya" : "") + (c === hoy ? " hoy" : "") + (c < hoy ? " pasado" : "")}
							onClick={() => alternar(c)}>{Number(c.substring(8))}</button>
					))}
				</div>
			</div>
			<div style={{ minWidth: 0 }}>
				<div className="wte-base">
					<label className="wau-campo"><span className="wau-lab">{S.entrada}</span>
						<HoraSelect value={b.startTime} onChange={v => setB(x => ({ ...x, startTime: v }))} label={S.entrada} /></label>
					<label className="wau-campo"><span className="wau-lab">{S.salida}</span>
						<HoraSelect value={b.endTime} onChange={v => setB(x => ({ ...x, endTime: v }))} label={S.salida} /></label>
					<label className="wau-campo"><span className="wau-lab">{S.aula}</span>{selAula(b.classroomId, n => setB(x => ({ ...x, classroomId: n })), S.aula)}</label>
					<label className="wau-campo"><span className="wau-lab">{S.maestra}</span>{selMaestra(b.mentorId, n => setB(x => ({ ...x, mentorId: n })), S.maestra)}</label>
					<button type="button" className="wau-btn s mini" disabled={value.length === 0} onClick={aplicarATodos}>{S.aplicarATodos}</button>
				</div>
				{value.length === 0 ? <div className="wte-dias-vacio">{S.tocaLosDias}</div> : (
					<div className="wte-lista-dias">
						{value.map(d => {
							const e = etiquetaDia(d.date);
							const h = horasEntre(d.startTime, d.endTime);
							return (
								<div key={d.date} className="wte-fila-dia">
									<span className="dia">{e.dia}<small>{e.anio}</small></span>
									<HoraSelect value={d.startTime} onChange={v => cambiar(d.date, "startTime", v)} label={S.entrada + " " + e.dia} />
									<HoraSelect value={d.endTime} onChange={v => cambiar(d.date, "endTime", v)} label={S.salida + " " + e.dia} />
									{selAula(d.classroomId, n => cambiar(d.date, "classroomId", n), S.aula + " " + e.dia)}
									{selMaestra(d.mentorId, n => cambiar(d.date, "mentorId", n), S.maestra + " " + e.dia)}
									<span className={"hrs" + (h > 0 ? "" : " wau-oro")}>{h > 0 ? horasTexto(h) : S.horaMal}</span>
									<button type="button" className="wte-quitar" aria-label={S.quitarDia(e.dia)} title={S.quitarDia(e.dia)} onClick={() => alternar(d.date)}>×</button>
								</div>
							);
						})}
					</div>
				)}
				{value.length > 0 ? <div className="wte-dias-total"><span>{S.nDias(value.length)}</span><span className="wau-num">{horasTexto(total)}</span></div> : null}
			</div>
		</div>
	);
};

export default DiasEditor;
